import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Global, silent delivery worker for Smart Reminders.
// While the app is open and the user is logged in, it polls for DUE reminders and
// — ONLY if the browser has granted Notification permission — fires a real Web
// Notification, then confirms delivery to the server (/reminders/{id}/fire). We
// never mark a reminder "notified" unless the browser actually showed it.
export function ReminderNotifier() {
  const { user } = useAuth();
  const firedRef = useRef(new Set());

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      let list;
      try {
        ({ data: list } = await api.get("/reminders"));
      } catch {
        return;
      }
      const due = (list || []).filter((r) => r.due && !firedRef.current.has(r.reminder_id));
      for (const r of due) {
        try {
          const n = new Notification(r.title || "EVERMIN reminder", {
            body: r.description || "You have a reminder due now.",
            tag: r.reminder_id,
            icon: "/favicon.svg",
          });
          n.onclick = () => {
            window.focus();
            window.location.assign("/reminders");
            n.close();
          };
          firedRef.current.add(r.reminder_id);
          // Confirmed delivery → record it (advances recurring to next occurrence).
          await api.post(`/reminders/${r.reminder_id}/fire`).catch(() => {});
          window.dispatchEvent(new CustomEvent("evermin:reminder-fired", { detail: { id: r.reminder_id } }));
        } catch {
          /* ignore a single failed notification */
        }
      }
    };

    tick();
    const iv = setInterval(tick, 45000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  return null;
}
